import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, FilterIcon, LinkIcon, ImageIcon, InstagramIcon, LinkedinIcon, MagnetIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, SparklesIcon, TargetIcon, TrendingUpIcon, UsersIcon, VideoIcon, YoutubeIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { SOCIAL_NETWORKS } from '../../data/socialNetworks';
import { DynamicForm, DynamicFormResult } from './DynamicForm';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon,
  Bot: BotIcon,
  Magnet: MagnetIcon,
  Filter: FilterIcon,
  TrendingUp: TrendingUpIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};

/**
 * Destino del CTA principal: el formulario multipaso de cualificación. Si la
 * landing no lo incluye, baja al calendario.
 */
function scrollToForm() {
  const target = document.getElementById('formulario') ?? document.getElementById('calendario');
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}

/** Interruptores del constructor: guardan «si» / «no» o un booleano. */
function flag(block: LandingBlock, key: string, fallback: boolean): boolean {
  const value = block.config[key];
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) !== 'no';
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 w-full rounded-xl px-8 transition-transform duration-200 sm:w-auto ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-369" data-mp="10vyjao3u6oz2" /> : undefined} data-id="element-368" data-mp="n4tobzbeykv2">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-370" data-mp="1ggi77a20fg6k">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-371" data-mp="sqxoms9rqkxt">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text
}: {
  text: string;
}) {
  return <div className="mt-6 flex items-start justify-center gap-2 sm:gap-3" data-id="element-372" data-mp="11snevurwzdu0">
      <svg width="48" height="34" viewBox="0 0 48 34" fill="none" aria-hidden="true" className="mt-1 flex-none text-primary" data-id="element-373" data-mp="wqy3uf4oz05x">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-374" data-mp="2d4obtwyxm8ku" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-375" data-mp="289imspa8gdsj" />
      </svg>
      <p className="font-hand text-xl text-primary sm:text-2xl" data-id="element-376" data-mp="2kq0goprd0sm0">{text}</p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const surfaceClass = theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  const blocks = config.blocks.filter(b => b.visible);

  /** Logotipo de la empresa, con altura configurable por bloque. */
  function mark(height = 30) {
    return <BrandLogo src={config.logoUrl} size={height} alt={landing.name} data-id="element-377" data-mp="2ulv5k2m6fjx8" />;
  }
  const brandMark = mark();
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        {
          const ctaLabel = cfg(block, 'ctaLabel', '');
          const centered = cfg(block, 'logoAlign', 'center') === 'center';
          const showLogo = flag(block, 'showLogo', true);
          const logoHeight = Number(cfg(block, 'logoHeight', 34)) || 34;
          const actions = <div className="flex items-center gap-2 sm:gap-4" data-id="element-378" data-mp="1dcg16iaz5x5u">
            {authLinks && <Link to="/login" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-id="element-379" data-mp="19bmfzwox2wgn">
                Iniciar sesión
              </Link>}
            {ctaLabel && <Button size="sm" onClick={scrollToForm} className="rounded-full px-5" data-id="element-380" data-mp="212tvkfh1883l">
                {ctaLabel}
              </Button>}
          </div>;
          const hasActions = Boolean(ctaLabel) || authLinks;
          return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-381" data-mp="1s0kltrtl8ir9">
            {centered ?
            // Logo centrado: las acciones se reservan su propio espacio a
            // izquierda y derecha, así nunca se solapan con el logotipo.
            <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-3 px-5 py-4 sm:px-8" data-id="element-382" data-mp="2sau2c4vq21c7">
                <span aria-hidden="true" data-id="element-383" data-mp="2x69f0thztr71" />
                <div className="flex justify-center" data-id="element-384" data-mp="319p7ddn155dx">{showLogo && mark(logoHeight)}</div>
                <div className="flex justify-end" data-id="element-385" data-mp="36178q2syt7zf">{hasActions && actions}</div>
              </div> : <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 sm:px-8" data-id="element-386" data-mp="3azqvu6jaoptd">
                {showLogo ? mark(logoHeight) : <span data-id="element-387" data-mp="3fxsbehhii703" />}
                {hasActions && actions}
              </div>}
          </header>;
        }
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-388" data-mp="3kvtvn5qoccd5">
            {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-5 pb-6 pt-16 text-center sm:px-8 sm:pt-24" data-id="element-389" data-mp="1uls9sth7461o">
            <div className="mx-auto max-w-4xl" data-id="element-390" data-mp="2hzlix5qyg23p">
              {flag(block, 'showLogo', false) && <div className="mb-12 flex justify-center" data-id="element-391" data-mp="2m05fo87m93pb">
                  {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
                </div>}
              {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary sm:text-xs" data-id="element-392" data-mp="2vy31kp206oer">
                  {eyebrow}
                </p>}
              {cfg(block, 'brandLine', '') && <p className="mt-4 text-[11px] font-bold uppercase leading-relaxed tracking-[0.22em] text-navy sm:text-[13px] sm:tracking-[0.3em]" data-id="element-393" data-mp="34y868rftb8p8">
                  {cfg(block, 'brandLine', '')}
                </p>}
              <h1 className="mx-auto mt-7 max-w-3xl whitespace-pre-line text-[32px] font-bold leading-[1.08] tracking-[-0.025em] text-navy sm:mt-9 sm:text-[54px]" data-id="element-394" data-mp="3eokpmgbw4vts">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-395" data-mp="3jlb634rlxtfq" />
              </h1>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-6 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:mt-8 sm:text-base" data-id="element-396" data-mp="1czex5xkum7x8">
                  <Highlighted text={cfg(block, 'subtitle', '')} highlight={cfg(block, 'subtitleHighlight', '')} data-id="element-397" data-mp="17yzldag7t8hd" />
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-398" data-mp="y1uyt9igjb01">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-399" data-mp="1qrbk7n7ntrmr">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-400" data-mp="1lz1x0exxj5o6">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-401" data-mp="2egmpnhzyga78">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-402" data-mp="29p4lkl2mxti1">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-403" data-mp="7tyl4zzlmqyg">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-404" data-mp="3u76s8mvx6g92" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-405" data-mp="2zkv7d3phc16">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        return <section key={block.id} className="px-5 pb-4 pt-10 sm:px-8 sm:pb-8" data-id="element-406" data-mp="3b9mdi8mvcy26">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} overlayTitle={cfg(block, 'overlayTitle', '') ? <Highlighted text={cfg(block, 'overlayTitle', '')} highlight={cfg(block, 'overlayHighlight', '')} data-id="element-408" data-mp="19hr6w7fgzjqt" /> : undefined} maxWidthClass="max-w-[760px]" data-id="element-407" data-mp="3g6mbrxdh8wiq" />
            {cfg(block, 'note', '') && <HandwrittenNote text={cfg(block, 'note', '')} data-id="element-409" data-mp="2yyv53wvwh8us" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-410" data-mp="2u01ss7scnnw7">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mx-auto mt-9 flex max-w-[380px] justify-center" data-id="element-411" data-mp="33wwpcl52be7u">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-412" data-mp="3ho1qxsrzqgkt" />
              </div>}
          </section>;
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-413" data-mp="3mopgsb4hgrdg">
            <div className="mx-auto max-w-6xl" data-id="element-414" data-mp="1x9iy0kfinets">
              {cfg(block, 'title', '') && <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-415" data-mp="1saeqrzc5jr63">
                  {cfg(block, 'title', '')}
                </h2>}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-416" data-mp="obsvynyuu1t5">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`group rounded-2xl border border-border bg-background p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-417" data-mp="xchb5lzzi87m" data-mpi={index}>
                      <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15" data-id="element-418" data-mp="5kia4l7zkf84">
                        <Icon className="h-6 w-6 text-primary" data-id="element-419" data-mp="edljoifig7xh" />
                      </span>
                      <h3 className="mb-3 text-base font-semibold text-navy" data-id="element-420" data-mp="efg1hnvhjjxc">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-421" data-mp="3i1n5qbnketj4">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'pilares':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-10 sm:px-8 sm:py-14" data-id="element-422" data-mp="2wlik6h3ign9s">
            <div className="mx-auto max-w-5xl rounded-3xl bg-surface px-6 py-12 sm:px-10 sm:py-14" data-id="element-423" data-mp="2rlw84azf5d2k">
              <div className="grid gap-10 sm:grid-cols-3 sm:gap-0" data-id="element-424" data-mp="2ms1t3kurdm9t">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`px-2 text-center sm:px-7 ${index > 0 ? 'sm:border-l sm:border-border/70' : ''}`} data-id="element-425" data-mp="2du7we8t30qm4" data-mpi={index}>
                      <Icon className="mx-auto mb-6 h-9 w-9 text-primary" aria-hidden="true" data-id="element-426" data-mp="23uqwy94fb8yl" />
                      <h3 className="mb-4 text-lg font-bold text-navy" data-id="element-427" data-mp="1u74ryxsizjlx">
                        {item.title}
                      </h3>
                      <p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground" data-id="element-428" data-mp="1z797ollm0ab3">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'frase':
        return <section key={block.id} className="px-5 py-12 sm:px-8 sm:py-16" data-id="element-429" data-mp="2cynrgunccfj7">
            <div className="mx-auto flex max-w-4xl items-center gap-5 sm:gap-8" data-id="element-430" data-mp="2hx8t8yi8r79r">
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-431" data-mp="237t5zshx5jyt" />
              <p className="flex-1 whitespace-pre-line text-center text-[11px] font-bold uppercase leading-relaxed tracking-[0.16em] text-navy sm:flex-none sm:text-sm sm:tracking-[0.18em]" data-id="element-432" data-mp="235ysv0cw2w5a">
                {cfg(block, 'text', '')}
              </p>
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-433" data-mp="30o8mibwrr38u" />
            </div>
          </section>;
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-434" data-mp="yyaz8kjyeln2">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-435" data-mp="tyerca84ya28">
              {items.map(item => <div key={item.label} data-id="element-436" data-mp="68uabwvjlmvl">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-437" data-mp="69vs0k72d7ma">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-438" data-mp="fajymtu4o5p5">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-439" data-mp="3g7e01f816bsn">
            <div className="mx-auto max-w-6xl" data-id="element-440" data-mp="3l67dj7nbi2st">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-441" data-mp="3q4indidm4db1">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-442" data-mp="3uxbqk1tfndl0">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-443" data-mp="2qwo82iftxbk">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-444" data-mp="cp2ranfzd9i0">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-445" data-mp="2ehr0ddfb2r5p">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-446" data-mp="10kxolj7pyjqr">
                      {item.avatar ? <img src={item.avatar} alt="" className="h-11 w-11 rounded-full object-cover" data-id="element-447" data-mp="vtpfype7r2en" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-448" data-mp="rsnfk58k8kb3">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-449" data-mp="1jk3bipw0bjdz">
                        <span className="block truncate font-semibold text-foreground" data-id="element-450" data-mp="1fi8a7le2t607">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-451" data-mp="1ahk8o5s816rq">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-452" data-mp="3vadvzsdfaagw">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-453" data-mp="3s7uz9qyvnwz8">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-454" data-mp="3n99xvo7w3797">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-455" data-mp="js1hl8st8fgl">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-456" data-mp="erniwhd3mhv8">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-457" data-mp="2zk7d47hykyah">
            <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-458" data-mp="2um5ufnog30zj">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-459" data-mp="1a3daqm998ak4">
                {cfg(block, 'title', 'Solicita información')}
              </h2>
              <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-460" data-mp="1jzoqxnbytkju">
                {cfg(block, 'subtitle', '')}
              </p>
            </div>
            {form ? <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-461" data-mp="1tprq659ojuqm" /> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-462" data-mp="1xt7kuw26vl0o">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'faq':
        {
          const items = cfg<{
            q: string;
            a: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-463" data-mp="5atxv65c8w2m">
            <div className="mx-auto max-w-3xl" data-id="element-464" data-mp="5cn5oorotmc7">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-465" data-mp="1aa2a70ey0il">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-466" data-mp="a9nayxgpql29">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-467" data-mp="ac9i4vzoyyjy">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-468" data-mp="k7b8tw9m88on">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-469" data-mp="17x5jq8adjn69" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-470" data-mp="1mknikq199nkr">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        {
          // La reserva usa el calendario global de la empresa salvo que la
          // landing tenga uno propio. La lógica de reserva no cambia.
          const custom = cfg(block, 'calendarSource', 'global') === 'custom';
          const bookingUrl = String((custom ? cfg(block, 'url', '') : cfg(block, 'globalUrl', '') || cfg(block, 'url', '')) as string).trim();
          // Sin enlace válido el bloque no se muestra en la landing pública:
          // así nunca queda un hueco ni se carga ningún script de calendario.
          if (!bookingUrl && !preview) return null;
          const embedded = cfg(block, 'calendarMode', 'boton') === 'incrustado' && Boolean(bookingUrl);
          const embedHeight = Number(cfg(block, 'embedHeight', 700)) || 700;
          if (embedded) {
            return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-471" data-mp="1s9m0z3v5iwdr">
              <div className="mx-auto w-full max-w-3xl text-center" data-id="element-472" data-mp="1ihq39twz98a2">
                <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-473" data-mp="1ifvhyehsne5g">
                  {cfg(block, 'title', 'Agenda una llamada')}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-474" data-mp="18klb9dggailc">
                  {cfg(block, 'text', '')}
                </p>
                <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-background shadow-soft" data-id="element-475" data-mp="zic5t201hfet">
                  <iframe src={bookingUrl} title={cfg(block, 'title', 'Calendario de reservas')} className="w-full" style={{
                    height: `${embedHeight}px`
                  }} loading="lazy" data-id="element-476" data-mp="uksr60zfj0mc" />
                </div>
              </div>
            </section>;
          }
          return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-477" data-mp="2wi2ymcvnviaf">
            <div className="mx-auto w-full max-w-3xl rounded-3xl border border-border bg-background p-7 text-center shadow-soft sm:p-12" data-id="element-478" data-mp="31fcmvp7bdnwo">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" aria-hidden="true" data-id="element-479" data-mp="3697ah3q1in11" />
              <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-480" data-mp="3b8aa28n9411m">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-481" data-mp="2iibdztan33ng">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-9 flex justify-center" data-id="element-482" data-mp="2sg928gsi10g2">
                <Button size="lg" className="h-14 w-full rounded-xl px-8 sm:w-auto" onClick={() => {
                  if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener');
                }} data-id="element-483" data-mp="2sdmuag1p4ixb">
                  {cfg(block, 'ctaLabel', 'Reservar hora')}
                </Button>
              </div>
              {!bookingUrl && <p className="mt-5 text-xs text-muted-foreground" data-id="element-484" data-mp="1ytiqac3xmojj">
                  Conecta Google Calendar en Integraciones o añade una URL de
                  reserva a este bloque.
                </p>}
            </div>
          </section>;
        }
      case 'whatsapp':
        return <a key={block.id} href={`https://wa.me/${String(cfg(block, 'number', '')).replace(/\D/g, '')}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-485" data-mp="28pvl5db7r8fv">
            <MessageCircleIcon className="h-6 w-6" data-id="element-486" data-mp="1p1wcc807mbd3" />
          </a>;
      case 'cta_final':
        {
          // Con número de WhatsApp el botón abre el chat; si no, baja al formulario.
          const waNumber = String(cfg(block, 'whatsappNumber', '')).replace(/\D/g, '');
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-487" data-mp="n6jho8h40fjd">
            <div className="mx-auto max-w-3xl text-center" data-id="element-488" data-mp="s0nsx3xer9q9">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-489" data-mp="dbhsxdqqxfhb">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-490" data-mp="i76u1liez4fq">
                {cfg(block, 'text', '')}
              </p>
              <div className="mx-auto mt-10 flex max-w-[380px] justify-center" data-id="element-491" data-mp="1axxdioegyt93">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={() => {
                  if (waNumber) {
                    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(cfg(block, 'whatsappMessage', ''))}`, '_blank', 'noopener');
                    return;
                  }
                  scrollToForm();
                }} data-id="element-492" data-mp="w0y733vct6g7" />
              </div>
            </div>
          </section>;
        }
      case 'footer':
        {
          // Solo se muestran las redes con URL configurada.
          const socials = cfg<{
            network: string;
            url: string;
          }[]>(block, 'socials', []).filter(s => s.url?.trim());
          return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-493" data-mp="2ozy21skt2ukl">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-494" data-mp="2p2areqsw877o">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-495" data-mp="3gqm1n06tzrw5">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-496" data-mp="3bz2tsy6hblp4">
                <p data-id="element-497" data-mp="37yaky61iritd">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-498" data-mp="8b2aq7tmulk9">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-499" data-mp="3jj60eokuvhg">
                      {link}
                    </span>)}
                </div>
              </div>
              {socials.length > 0 && <div className="flex gap-3 text-muted-foreground" data-id="element-500" data-mp="1r7gka94nkvac">
                  {socials.map((social, index) => {
                  const network = SOCIAL_NETWORKS[social.network];
                  const Icon = network?.icon ?? LinkIcon;
                  return <a key={`${social.network}-${index}`} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={network?.label ?? social.network} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" data-id="element-501" data-mp="2567tbh4nrqrm" data-mpi={index}>
                        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-502" data-mp="2ixcu4mjrip3j" />
                      </a>;
                })}
                </div>}
            </div>
          </footer>;
        }
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-503" data-mp="3t918q0urzcnh">
            <div className="mx-auto max-w-6xl" data-id="element-504" data-mp="21x3nbi6y0hp">
              {cfg(block, 'title', '') && <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-505" data-mp="3k7jsmihdbays">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-506" data-mp="gqt5gqtnmh9v">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className={`grid gap-6 md:grid-cols-3 ${cfg(block, 'title', '') || cfg(block, 'subtitle', '') ? 'mt-14' : ''}`} data-id="element-507" data-mp="708bjgoxe0xa">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-508" data-mp="zn2uuaa0au96" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-509" data-mp="1drjlmebdm4fa">
                        <Icon className="h-5 w-5 text-primary" data-id="element-510" data-mp="1njpm6cfp1u63" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-511" data-mp="1ikmhwu7jfrz6">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-512" data-mp="uw3a0fsehh7m">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-513" data-mp="2g6touarbdwep">
            <div className="mx-auto max-w-4xl" data-id="element-514" data-mp="2b91wjfmrc89z">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-515" data-mp="2jaizfzfkr3eq">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-516" data-mp="2saxl4zwgakir">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-517" data-mp="2x9r3b5momzp9" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-518" data-mp="2x7o9sbmbmofs">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-519" data-mp="24kttzs0g8d8n" />
                    </span>
                    <span data-id="element-520" data-mp="29kg8e4s0jzj1">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-521" data-mp="29j54m4ykirrx">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-522" data-mp="2ecpv7bxu0jh3">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-523" data-mp="19cj5xtsio75v">
            <div className="mx-auto max-w-6xl" data-id="element-524" data-mp="14blpbln5nl6h">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-525" data-mp="zelkfe21kogy">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-526" data-mp="ql8ijmm02ciq">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-527" data-mp="lk2n3h0ygb5i" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-528" data-mp="bor7yxlmx1x8">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-529" data-mp="7m4dsq1hr4wv" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-530" data-mp="dn2c7hz3r1sv">
            <div className="mx-auto max-w-5xl" data-id="element-531" data-mp="ilm21tgtgxqi">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-532" data-mp="inglf3cfwhsh">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-533" data-mp="8o7smdtb997p">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-534" data-mp="2320hbek4h50a">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-535" data-mp="3jifzy4viglp9" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-536" data-mp="3sj4b8rtis7yg">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-537" data-mp="63t1nlev0x28">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-538" data-mp="k2srgwe3zhzz">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-539" data-mp="k0yga9sn3493">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-540" data-mp="2lvgw9a6eidhv">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-541" data-mp="ksg4c59synx1">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-542" data-mp="pscaaa228pf8">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-543" data-mp="upks29jaqrfy" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-544" data-mp="13q0tzeke5qip">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-545" data-mp="1ihriyt69667z">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-546" data-mp="1mfguw2zznkzn">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-547" data-mp="3bynsh33gofl0">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-548" data-mp="36y17jt5qm0bs">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-549" data-mp="32ztzdmpca7c7">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-550" data-mp="3gxs0vrika9aa">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-551" data-mp="i8a0ryp3ozh0" />
                      <span className="break-words text-sm text-foreground" data-id="element-552" data-mp="dhbfslt63d7f">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const style = wrapperStyle(block);
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle ? <div key={block.id} style={style} data-id="element-553" data-mp="9j1cw78sjakf">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} style={style} className={`relative cursor-pointer outline-none transition-shadow ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-554" data-mp="3rnw8ma1uezee">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-555" data-mp="2ctei5qpp593r">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  return <div className="w-full bg-background" style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`
  } as React.CSSProperties} data-id="element-556" data-mp="2hrzidq4y7ss7">
      {blocks.map(renderEditable)}
      {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-557" data-mp="1ozwcydbo546d">
          Esta landing no tiene bloques visibles. Activa alguno desde el editor.
        </div>}
    </div>;
}